import React from 'react';
import { Text, Colors, Paper } from 'library';
import { Grid } from '@material-ui/core';
import { UpcomingJobModel } from '../nestedJobList/jobListModels';
import moment from 'moment';
import JobCardChip from 'parent-portal/home/newsfeed/jobCard/JobCardChip';
import { ActiveJobActions } from './ActiveJobActions';
import { MoreOptions } from '../JobCardMoreOptions';
import { useUserContext } from 'UserContext';

const JobCard = ({ job }: { job: UpcomingJobModel }) => {
    const { getDisplayText } = useUserContext();
    function formatDate() {
        if (job.start_month) {
            return job.start_month;
        }
        let endDate = moment(job.end_date, 'YYYY-MM-DD');
        let formattedEndDate = endDate.format('MMM D, YYYY');

        if (!job.ongoing && !job.all_day) {
            return String(formattedEndDate);
        }

        let startDate = moment(job.start_date, 'YYYY-MM-DD');
        let formattedStartDate = startDate.format('MMM D');
        return String(formattedStartDate) + ' - ' + String(formattedEndDate);
    }

    function activeDaysText() {
        if (job.pay_by_day) {
            let days = job.paid_days ?? 0;
            if (job.status === 'ACTIVE' && job.has_applicants) {
                days += 1;
            }
            return <Text variant="caption">Days active this pay period (Fri-Thurs): {days}</Text>;
        }
        return <></>;
    }

    function ColoredHeader() {
        let colorKey = job.business_job_type;
        let colors: { [key: string]: { color: string; text: string } } = {
            PERMANENT: { color: Colors.darkNavy, text: 'Permanent' },
            SUBSTITUTE: { color: Colors.turquoise, text: getDisplayText('substitute').capitalize() },
            TRIAL_RUN: { color: Colors.terraCotta, text: 'Trial Run' },
            DRAFT: { color: Colors.mediumRed, text: 'Draft' },
        };
        let color = job.status === 'DRAFT' ? colors.DRAFT : colorKey ? colors[colorKey as string] : null;

        return color ? (
            <Grid
                container
                justify="center"
                alignItems="center"
                style={{
                    height: 30,
                    backgroundColor: color.color,
                    borderTopLeftRadius: 18,
                    borderTopRightRadius: 18,
                }}
            >
                <Text variant="body2" bold color={Colors.white}>
                    {color.text}
                </Text>
            </Grid>
        ) : null;
    }

    return (
        <Grid item container xs={12}>
            <ColoredHeader />
            <Grid xs={12} style={{ padding: 20 }}>
                <Grid container direction="row" justify="space-between">
                    <Grid xs={10}>
                        {activeDaysText()}
                        <Text variant="h1" textStyle={{ wordBreak: 'break-word' }}>
                            {job.headline}
                        </Text>
                    </Grid>
                    <MoreOptions job={job} past={false} oneTime={!job.ongoing} review={false} jobId={job.id} />
                </Grid>
                {!job.on_call && !job.pay_by_day ? (
                    <Text
                        variant="h2"
                        textStyle={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                    >
                        {formatDate()}
                    </Text>
                ) : null}
                {job.business_job_type ? (
                    <Text variant="caption" textStyle={{ wordBreak: 'break-word' }}>
                        {job.address.street} {job.address.city}, {job.address.state} {job.address.zip_code}
                    </Text>
                ) : null}
                <Grid container direction="row" style={{ marginBottom: 10, marginTop: 8 }}>
                    <JobCardChip
                        color={Colors.turquoise}
                        textColor={Colors.white}
                        text={job.ongoing ? 'WEEKLY' : job.on_call ? 'ON-CALL' : 'ONE-TIME'}
                    />
                    <JobCardChip
                        style={{ marginLeft: 5 }}
                        color={Colors.turquoise}
                        textColor={Colors.white}
                        text={job.job_type_name.toUpperCase()}
                    />
                </Grid>
                {job.status !== 'DRAFT' ? <ActiveJobActions job={job} /> : null}
            </Grid>
        </Grid>
    );
};

export default JobCard;
